import { useState } from "react";
import { getNamespaceTranslations } from "../lib/questionnaire-translations";
import { Header } from "./Header";
import { ProgressBar } from "./ui/ProgressBar";
import { US_STATES } from "../lib/constants";
import { isPuertoRico } from "../lib/puerto-rico-check";
import type { ProgressSectionId } from "../lib/progress-calculator";

interface StateCheckScreenProps {
  onContinue: (state: string) => void;
  onBack?: () => void;
  language?: "en" | "es";
  logoUrl?: string;
  currentStep: number;
  totalSteps: number;
  sectionIndex: number;
  totalSections: number;
  sectionId: ProgressSectionId;
  initialState?: string;
}

const SHORTLIST_STATES = [
  "California",
  "Florida",
  "Texas",
  "New York",
  "Illinois",
  "New Jersey",
  "Colorado",
  "Georgia",
];

const OTHER_STATES = US_STATES.filter((state) => !SHORTLIST_STATES.includes(state));

const testIdForState = (state: string) => state.toLowerCase().replace(/\s+/g, "-");

export function StateCheckScreen({
  onContinue,
  onBack,
  language = "en",
  logoUrl,
  currentStep,
  totalSteps,
  sectionIndex,
  totalSections,
  sectionId,
  initialState = "",
}: StateCheckScreenProps) {
  const t = getNamespaceTranslations(language, "stateCheck");
  const [selectedState, setSelectedState] = useState(initialState);
  const [isOtherOpen, setIsOtherOpen] = useState(false);

  const isPR = isPuertoRico(selectedState);
  const isOtherSelected = !!selectedState && !SHORTLIST_STATES.includes(selectedState);

  const handleSelect = (state: string) => {
    setSelectedState(state);
    setIsOtherOpen(false);

    if (!isPuertoRico(state)) {
      onContinue(state);
    }
  };

  return (
    <div className="w-full min-h-screen flex flex-col bg-gradient-to-b from-[#FAFAFA] to-[#E8F0F3]">
      <Header logoUrl={logoUrl} onBack={onBack} backButtonTestId="state_check-back_button" />

      <main
        className="flex-1 w-full py-[30px] px-5 lg:px-4 overflow-y-auto relative">
        <div className="flex justify-center lg:items-center lg:min-h-full">
          <div className="w-full max-w-[640px]">
            <ProgressBar
              currentStep={currentStep}
              totalSteps={totalSteps}
              sectionIndex={sectionIndex}
              totalSections={totalSections}
              sectionId={sectionId}
              language={language}
            />
            <div className="space-y-2.5 my-8">
              <h1
                className="text-[24px] sm:!text-[30px] leading-[30px] sm:!leading-[36px] font-normal text-black"
                style={{ fontFamily: "TT Commons Pro" }}
              >
                {t.title}
              </h1>
              <p
                className="text-[16px] leading-[22px] text-gray-700"
                style={{ fontFamily: "TT Commons Pro" }}
              >
                {t.description}
              </p>
          </div>
            <div className="mb-6" role="radiogroup" aria-label={t.label}>
              <div className="space-y-3">
                {SHORTLIST_STATES.map((state) => {
                  const selected = selectedState === state;
                  return (
                    <button
                      key={state}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      data-testid={`state_check-card-${testIdForState(state)}`}
                      onClick={() => handleSelect(state)}
                      className={`w-full h-[62px] px-5 rounded-xl border bg-white flex items-center text-left text-base leading-6 transition-colors ${
                        selected ? 'border-[#2D55FB]' : 'border-gray-300'
                      }`}
                      style={{ fontFamily: 'TT Commons Pro' }}
                    >
                      <span
                        className={`mr-3 w-5 h-5 rounded-full border flex items-center justify-center flex-shrink-0 ${
                          selected ? 'border-[#2D55FB]' : 'border-gray-400'
                        }`}
                      >
                        {selected && <span className="w-2.5 h-2.5 rounded-full bg-[#2D55FB]" />}
                      </span>
                      {state}
                    </button>
                  );
                })}
              </div>

             <div className="flex items-center my-4" aria-hidden="true">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="px-3 text-sm text-gray-400" style={{ fontFamily: 'TT Commons Pro' }}>
                  {t.orDivider}
                </span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              <div className="relative">
                <button
                  type="button"
                  data-testid="state_check-other_toggle"
                  onClick={() => setIsOtherOpen((open) => !open)}
                  aria-expanded={isOtherOpen}
                  className={`w-full h-[62px] px-5 rounded-md border bg-white text-left flex items-center justify-between ${
                    isPR ? 'border-[#E34D52]' : isOtherSelected || isOtherOpen ? 'border-[#2D55FB]' : 'border-gray-300'
                  }`}
                  style={{ fontFamily: 'TT Commons Pro' }}
                >
                  <span
                    className={`text-base leading-6 ${isOtherSelected ? 'text-black' : 'text-gray-400'}`}
                    style={{ fontFamily: 'TT Commons Pro' }}
                  >
                    {isOtherSelected ? selectedState : t.otherStateLabel}
                  </span>
                  <svg
                    width="14"
                    height="8"
                    viewBox="0 0 14 8"
                    fill="none"
                    className={`transition-transform ${isOtherOpen ? 'rotate-180' : ''}`}
                  >
                    <path d="M6.3125 7.34375L0.3125 1.34375C0 1.0625 0 0.59375 0.3125 0.28125C0.59375 0 1.0625 0 1.375 0.28125L6.84375 5.78125L12.3125 0.3125C12.5938 0 13.0625 0 13.375 0.3125C13.6562 0.59375 13.6562 1.0625 13.375 1.34375L7.34375 7.34375C7.0625 7.65625 6.59375 7.65625 6.3125 7.34375Z" fill="black"/>
                  </svg>
                </button>

                {isOtherOpen && (
                  <div className="absolute z-10 w-full bottom-full mb-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    {OTHER_STATES.map((state) => (
                      <button
                        key={state}
                        type="button"
                        data-testid={`state_check-other_option-${testIdForState(state)}`}
                        onClick={() => handleSelect(state)}
                        className="w-full px-3 py-2 text-left hover:bg-gray-100 text-base"
                        style={{ fontFamily: 'TT Commons Pro' }}
                      >
                        {state}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Error Message for Puerto Rico */}
              {isPR && (
                <div
                  className="mt-2 py-3 rounded text-[#E34D52] text-sm leading-[21px]"
                  style={{
                    fontFamily: 'TT Commons Pro'
                  }}
                >
                  {t?.errorPuertoRico}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
